import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faSun, faMoon, faTv, faMusic, faGaugeHigh } from "@fortawesome/free-solid-svg-icons";
import Tippy from "@tippyjs/react";
import { motion, AnimatePresence } from "framer-motion";

const SettingsToggle = () => {
    const { 
        isDark, 
        toggleTheme, 
        showFps, 
        setShowFps, 
        enableMusic, 
        setEnableMusic, 
        lowEndMode, 
        setLowEndMode 
    } = useTheme();

    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={containerRef}>
            <Tippy content="Cài đặt hệ thống" visible={isOpen ? false : undefined}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 border active:scale-95 z-20 ${
                        isOpen 
                            ? "bg-indigo-500 border-indigo-600 text-white shadow-lg"
                            : isDark
                                ? "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                    }`}
                    aria-label="System Settings"
                >
                    <FontAwesomeIcon icon={faGear} className={`text-base transition-transform duration-500 ${isOpen ? "rotate-90" : "rotate-0"}`} />
                </button>
            </Tippy>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute right-0 mt-3.5 w-64 p-4 rounded-xl border bg-white/80 dark:bg-slate-900/85 backdrop-blur-2xl border-slate-200/80 dark:border-slate-700/80 shadow-[0_15px_35px_rgba(0,0,0,0.15)] dark:shadow-[0_15px_35px_rgba(0,0,0,0.4)] ring-1 ring-black/5 dark:ring-white/10 z-[99999]"
                    >
                        {/* Popover Arrow */}
                        <div className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-white/80 dark:bg-slate-900/85 border-t border-l border-slate-200/80 dark:border-slate-700/80"></div>

                        <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 select-none">
                            Cài đặt nhanh
                        </h4>
                        
                        <div className="flex flex-col gap-3">
                            {/* Dark Mode */}
                            <div className="flex items-center justify-between py-1 border-b border-slate-100 dark:border-slate-800/50 pb-2">
                                <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-200">
                                    <FontAwesomeIcon icon={isDark ? faMoon : faSun} className="w-4 text-center text-indigo-500 dark:text-indigo-400" />
                                    <span className="text-xs font-bold">Giao diện tối</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer select-none">
                                    <input 
                                        type="checkbox" 
                                        checked={isDark} 
                                        onChange={toggleTheme} 
                                        className="sr-only peer" 
                                    />
                                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-indigo-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                                </label>
                            </div>

                            {/* Show FPS */}
                            <div className="flex items-center justify-between py-1 border-b border-slate-100 dark:border-slate-800/50 pb-2">
                                <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-200">
                                    <FontAwesomeIcon icon={faGaugeHigh} className="w-4 text-center text-emerald-500 dark:text-emerald-400" />
                                    <span className="text-xs font-bold">Hiển thị FPS</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer select-none">
                                    <input 
                                        type="checkbox" 
                                        checked={showFps} 
                                        onChange={() => setShowFps(!showFps)} 
                                        className="sr-only peer" 
                                    />
                                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-indigo-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                                </label>
                            </div>

                            {/* Music Player */}
                            <div className="flex items-center justify-between py-1 border-b border-slate-100 dark:border-slate-800/50 pb-2">
                                <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-200">
                                    <FontAwesomeIcon icon={faMusic} className="w-4 text-center text-pink-500 dark:text-pink-400" />
                                    <span className="text-xs font-bold">Trình phát nhạc</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer select-none">
                                    <input 
                                        type="checkbox" 
                                        checked={enableMusic} 
                                        onChange={() => setEnableMusic(!enableMusic)} 
                                        className="sr-only peer" 
                                    />
                                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-indigo-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                                </label>
                            </div>

                            {/* Low-end Mode */}
                            <div className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-200">
                                    <FontAwesomeIcon icon={faTv} className="w-4 text-center text-amber-500 dark:text-amber-400" />
                                    <span className="text-xs font-bold">Tối ưu máy yếu</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer select-none">
                                    <input 
                                        type="checkbox" 
                                        checked={lowEndMode} 
                                        onChange={() => setLowEndMode(!lowEndMode)} 
                                        className="sr-only peer" 
                                    />
                                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-indigo-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                                </label>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SettingsToggle;
